import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AgentMapDto, AgentMapSeatDto } from 'types';
import { AGENT_STATUS } from 'utils';

// Add dashboard state interface
interface DashboardState {
    callStats: Record<string, any>[];
    chatStats: Record<string, any> | null;
}

// Mở rộng state type để bao gồm cả timers
export interface AgentMapState {
    // Selected map IDs
    selectedMaps: number[];

    // Map data with seats
    mapData: {
        mapId: number;
        name?: string;
        seats: AgentMapSeatDto[];
    }[];

    // Counters - dùng string key cho object
    counters: Record<string, { call: number, chat: number }>;

    // Thêm timers - lưu timestamp khi agent thay đổi trạng thái
    timers: Record<string, number>;

    // Loading states
    loading: boolean;
    error: string | null;

    // Thêm trạng thái để theo dõi socket updates
    socketUpdateInProgress: boolean;
    lastSocketUpdateTime: number | null;

    // Add dashboard state
    dashboard: DashboardState;

    onlineDataForChart: AgentMapSeatDto[]; // Ensure this is correctly typed

    silentChartUpdate?: boolean; // Added this line, check if it's used or can be removed

    // Added for chart-specific updates
    lastSocketChartUpdateTime: number;
}

// Initial state
const initialState: AgentMapState = {
    selectedMaps: [],
    mapData: [],
    counters: {},
    timers: {},
    loading: false,
    error: null,
    socketUpdateInProgress: false,
    lastSocketUpdateTime: null,
    // Initialize dashboard
    dashboard: {
        callStats: [],
        chatStats: null
    },
    onlineDataForChart: [],
    lastSocketChartUpdateTime: 0, // Initialize
};

// Helper để ánh xạ trạng thái thành thuộc tính hiển thị
const mapStatusToDisplay = (status: string) => {
    switch (status) {
        case AGENT_STATUS.AGENT_UNPAUSED:
            return {
                name: "Sẵn sàng",
                backgroundColor: "#06A77D",
                iconColor: '#FFFFFF',
                textColor: '#FFFFFF'
            };
        case AGENT_STATUS.AGENT_PAUSED:
            return {
                name: "Tạm nghỉ",
                backgroundColor: "#FF9500",
                iconColor: '#FFFFFF',
                textColor: '#FFFFFF'
            };
        case AGENT_STATUS.ONCALL:
            return {
                name: "Đàm thoại",
                backgroundColor: "#D1131D",
                iconColor: '#FFFFFF',
                textColor: '#FFFFFF'
            };
        case AGENT_STATUS.RINGING:
            return {
                name: "Đang reo",
                backgroundColor: "#AF52DE",
                iconColor: '#FFFFFF',
                textColor: '#FFFFFF'
            };
        case AGENT_STATUS.CREATING_TICKET:
            return {
                name: "Tạo phiếu",
                backgroundColor: "#1564E8",
                iconColor: '#FFFFFF',
                textColor: '#FFFFFF'
            };
        default:
            return {
                name: "Không bật",
                backgroundColor: "#EBEBED",
                iconColor: '#B2B9C4',
                textColor: '#000000'
            };
    }
};

// Create the slice
export const agentMapSlice = createSlice({
    name: 'agentMap',
    initialState,
    reducers: {
        // Set selected maps
        setSelectedMaps(state, action: PayloadAction<number[]>) {
            state.selectedMaps = action.payload;
        },

        // Add or update a map in the store
        updateMapData(state, action: PayloadAction<AgentMapDto>) {
            const map = action.payload;
            const index = state.mapData.findIndex(m => m.mapId === map.agentMapId);

            if (index >= 0) {
                // Update existing map
                state.mapData[index] = {
                    mapId: map.agentMapId,
                    name: map.name || state.mapData[index].name, // Preserve existing name if none provided
                    seats: map.seats || []
                };
            } else {
                // Add new map
                state.mapData.push({
                    mapId: map.agentMapId,
                    name: map.name, // Include name in new maps
                    seats: map.seats || []
                });
            }

            // Khởi tạo counters & timers cho tất cả seats
            if (map.seats) {
                map.seats.forEach(seat => {
                    if (seat.agentMapSeatId) {
                        const seatIdKey = seat.agentMapSeatId.toString();

                        // Init counters
                        if (!state.counters[seatIdKey]) {
                            state.counters[seatIdKey] = {
                                call: seat.totalCall || 0,
                                chat: seat.totalChat || 0
                            };
                        }

                        // Init timers từ agentChangeStatusDate
                        if (seat.agentChangeStatusDate && !state.timers[seatIdKey]) {
                            try {
                                // Parse date format: "2023-11-25T14:30:00.000Z"
                                const timestamp = new Date(seat.agentChangeStatusDate).getTime();
                                if (!isNaN(timestamp)) {
                                    state.timers[seatIdKey] = timestamp;
                                }
                            } catch (error) {
                                console.error(`Failed to parse date: ${seat.agentChangeStatusDate}`, error);
                            }
                        }
                    }
                });
            }
            state.lastSocketUpdateTime = Date.now();
        },

        // Update multiple maps
        setMapData(state, action: PayloadAction<AgentMapDto[]>) {
            // Chuyển sang cấu trúc nội bộ
            state.mapData = action.payload.map(map => ({
                mapId: map.agentMapId,
                seats: map.seats || []
            }));

            // Khởi tạo counters và timers cho tất cả seats
            const newCounters: Record<string, { call: number, chat: number }> = {};
            const newTimers: Record<string, number> = {};

            action.payload.forEach(map => {
                map.seats?.forEach(seat => {
                    if (seat.agentMapSeatId) {
                        const seatId = seat.agentMapSeatId.toString();

                        // Init counters
                        newCounters[seatId] = {
                            call: seat.totalCall || 0,
                            chat: seat.totalChat || 0
                        };

                        // Init timers
                        if (seat.agentChangeStatusDate) {
                            try {
                                const timestamp = new Date(seat.agentChangeStatusDate).getTime();
                                if (!isNaN(timestamp)) {
                                    newTimers[seatId] = timestamp;
                                }
                            } catch (error) {
                                console.error(`Failed to parse date: ${seat.agentChangeStatusDate}`, error);
                            }
                        }
                    }
                });
            });

            // Merge với state hiện có
            state.counters = { ...state.counters, ...newCounters };
            state.timers = { ...state.timers, ...newTimers };
        },

        // Cập nhật trạng thái của seat và timer
        updateSeatStatus(state, action: PayloadAction<{ seatId: number, status: string, timestamp: number }>) {
            const { seatId, status, timestamp } = action.payload;
            const seatIdKey = seatId.toString();

            // Cập nhật timer
            state.timers[seatIdKey] = timestamp;

            // Log what we're doing


            // Tìm và cập nhật seat trong maps
            let updated = false;
            for (let map of state.mapData) {
                const seatIndex = map.seats.findIndex(seat =>
                    seat.agentMapSeatId === seatId
                );

                if (seatIndex >= 0) {
                    // Cập nhật trạng thái seat
                    map.seats[seatIndex].statusName = status;
                    map.seats[seatIndex].agentChangeStatusDate = new Date(timestamp).toISOString();

                    // Cập nhật thuộc tính hiển thị theo trạng thái
                    const displayProps = mapStatusToDisplay(status);
                    map.seats[seatIndex].fillColor = displayProps.backgroundColor;
                    map.seats[seatIndex].backgroundColor = displayProps.backgroundColor;
                    map.seats[seatIndex].textColor = displayProps.textColor;
                    map.seats[seatIndex].iconColor = displayProps.iconColor;


                    updated = true;
                    break; // Dừng tìm kiếm khi đã tìm thấy seat
                }
            }

            if (!updated) {
                console.warn(`⚠️ Redux: Could not find seat ${seatId} to update status`);
            }
        },

        // Chỉ cập nhật timer (không thay đổi status)
        updateTimer(state, action: PayloadAction<{ seatId: number, timestamp: number }>) {
            const { seatId, timestamp } = action.payload;
            const seatIdKey = seatId.toString();

            // Cập nhật timer
            state.timers[seatIdKey] = timestamp;
        },

        // Tăng counter cho một seat
        incrementCounter(state, action: PayloadAction<{ seatId: number, counterType: 'call' | 'chat' }>) {
            const { seatId, counterType } = action.payload;
            const seatIdKey = seatId.toString();

            // Khởi tạo nếu chưa có
            if (!state.counters[seatIdKey]) {
                state.counters[seatIdKey] = { call: 0, chat: 0 };
            }

            // Tăng counter
            state.counters[seatIdKey][counterType]++;

            // Cập nhật giá trị trong map
            for (let map of state.mapData) {
                const seatIndex = map.seats.findIndex(seat =>
                    seat.agentMapSeatId === seatId
                );

                if (seatIndex >= 0) {
                    if (counterType === 'call') {
                        map.seats[seatIndex].totalCall = (map.seats[seatIndex].totalCall || 0) + 1;
                    } else {
                        map.seats[seatIndex].totalChat = (map.seats[seatIndex].totalChat || 0) + 1;
                    }
                    break;
                }
            }
        },

        // Loading states
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
        },

        setError(state, action: PayloadAction<string | null>) {
            state.error = action.payload;
        },

        // Reset all data
        resetData(state) {

            state.selectedMaps = [];
            state.mapData = [];
            state.counters = {};
            state.timers = {};
            state.loading = false;
            state.error = null;
        },

        // Cập nhật trạng thái của socket update
        setSocketUpdateInProgress(state, action: PayloadAction<boolean>) {
            state.socketUpdateInProgress = action.payload;
            if (action.payload) {
                state.lastSocketUpdateTime = Date.now();
            }
        },

        // Filter map data to only include the selected maps
        filterMapData(state, action: PayloadAction<{ mapIds: number[]; preserveOnline?: boolean }>) {
            const { mapIds, preserveOnline } = action.payload;
            const initialCount = state.mapData.length;

            // first, pull out any existing online‐map entry
            const existingOnline = state.mapData.find(m => m.mapId === -999);
            // then filter normally
            state.mapData = state.mapData.filter(m =>
                m.mapId === -999 && preserveOnline
                    ? true
                    : mapIds.includes(m.mapId)
            );

            // if we need to preserve “online” display but it isn’t in mapData, rebuild from chart data:
            if (preserveOnline && !state.mapData.some(m => m.mapId === -999)) {
                if (state.onlineDataForChart.length > 0) {
                    state.mapData.push({
                        mapId: -999,
                        name: 'Online',
                        seats: state.onlineDataForChart.map(seat => ({
                            ...seat,
                            canCall: seat.canCall !== undefined ? seat.canCall : true,
                            canChat: seat.canChat !== undefined ? seat.canChat : true,
                        }))
                    });
                }
            }



            state.lastSocketUpdateTime = Date.now();
        },

        // Add a dedicated action for clearing seat data on LOGGEDOFF
        clearSeatData(state, action: PayloadAction<number>) {
            const seatId = action.payload;
            const seatIdKey = seatId.toString();



            // Clear timer for this seat
            if (seatIdKey in state.timers) {
                delete state.timers[seatIdKey];

            }

            // Reset counters
            if (seatIdKey in state.counters) {
                state.counters[seatIdKey] = { call: 0, chat: 0 };

            }

            // Find and clear seat data in all maps (mapData)
            let foundAndUpdatedInMapData = false;
            for (let map of state.mapData) {
                const seatIndex = map.seats.findIndex(seat =>
                    seat.agentMapSeatId === seatId
                );

                if (seatIndex >= 0) {
                    // Store the original values for debugging
                    const originalSeat = map.seats[seatIndex];


                    // Create a completely new seat object with only essential properties
                    map.seats[seatIndex] = {
                        // Keep only location/position properties
                        agentMapSeatId: originalSeat.agentMapSeatId,
                        seatIp: originalSeat.seatIp || '',
                        seatName: originalSeat.seatName || '',
                        width: originalSeat.width || 96,
                        height: originalSeat.height || 96,
                        left: originalSeat.left || 0,
                        top: originalSeat.top || 0,

                        // Explicitly set all identity properties to null
                        extensionId: undefined,
                        shortName: undefined, // Ensure this is explicitly undefined
                        agentChangeStatusDate: undefined,
                        status: undefined,

                        // Reset counters
                        totalCall: 0,
                        totalChat: 0,

                        // Explicitly set status to LOGGEDOFF
                        statusName: "LOGGEDOFF",

                        // Set colors for LOGGEDOFF
                        fillColor: "#EBEBED",
                        backgroundColor: "#EBEBED",
                        textColor: "#000000",
                        iconColor: "#B2B9C4",
                        borderColor: "#B2B9C4"
                    };




                    foundAndUpdatedInMapData = true;
                    // No break here if seat could be in multiple maps, though unlikely for LOGGEDOFF
                }
            }
            if (!foundAndUpdatedInMapData) {
                console.warn(`⚠️ Redux: Could not find seat ${seatId} in mapData to clear for LOGGEDOFF status`);
            }

            // Remove from onlineDataForChart
            const seatIndexInChart = state.onlineDataForChart.findIndex(s => s.agentMapSeatId === seatId);
            if (seatIndexInChart !== -1) {
                state.onlineDataForChart.splice(seatIndexInChart, 1);

            }

            // Update timestamps
            state.lastSocketUpdateTime = Date.now();
            state.lastSocketChartUpdateTime = Date.now();
        },

        // Add a new comprehensive action to map all properties from socket to seat
        updateSeatComprehensive(state, action: PayloadAction<any>) {
            const data = action.payload;
            if (!data || !data.agentMapSeatId) {
                console.warn('⚠️ Redux: Received invalid data for comprehensive update', data);
                return;
            }

            const seatId = data.agentMapSeatId;
            const seatIdKey = seatId.toString();

            // Detailed logging of what Redux is receiving from the socket



            for (const key of Object.keys(data)) {

            }

            // Find the seat in the current state to check for status change
            let currentStatus = null;
            let currentSeat = null;

            // Find the current seat and its status
            for (const map of state.mapData) {
                const seat = map.seats.find(s => s.agentMapSeatId === seatId);
                if (seat) {
                    currentStatus = seat.statusName;
                    currentSeat = seat;
                    break;
                }
            }

            // Check if status is changing
            const newStatus = data.statusName || data.nextState;
            const isStatusChange = newStatus && currentStatus !== newStatus;

            // Reset timer for ANY status change EXCEPT LOGGEDOFF
            // We already handle LOGGEDOFF separately in clearSeatData action
            if (isStatusChange && newStatus !== "LOGGEDOFF") {

                state.timers[seatIdKey] = Date.now();
            }
            // If there's a timestamp provided, use it instead
            else if (data.parsedTimestamp) {
                state.timers[seatIdKey] = data.parsedTimestamp;
            }
            // Otherwise, use agentChangeStatusDate if provided
            else if (data.agentChangeStatusDate) {
                try {
                    const timestamp = new Date(data.agentChangeStatusDate).getTime();
                    if (!isNaN(timestamp)) {
                        state.timers[seatIdKey] = timestamp;
                    }
                } catch (e) {
                    console.error(`Failed to parse date: ${data.agentChangeStatusDate}`, e);
                }
            }

            // Find and update seat in all maps
            let foundAndUpdated = false;
            for (let map of state.mapData) {
                const seatIndex = map.seats.findIndex(seat =>
                    seat.agentMapSeatId === seatId
                );

                if (seatIndex >= 0) {
                    // Store original seat for logging
                    const originalSeat = { ...map.seats[seatIndex] };


                    // Create a NEW seat object with ALL properties from both the original seat and the update
                    const updatedSeat = {
                        ...originalSeat,  // Keep original properties
                        ...data           // Override with ALL properties from socket update
                    };

                    // If status/nextState changed, update colors too
                    if (data.statusName || data.nextState) {
                        const status = data.nextState || data.statusName;
                        const displayProps = mapStatusToDisplay(status);
                        updatedSeat.fillColor = displayProps.backgroundColor;
                        updatedSeat.backgroundColor = displayProps.backgroundColor;
                        updatedSeat.textColor = displayProps.textColor;
                        updatedSeat.iconColor = displayProps.iconColor;


                    }

                    // Log changes for debugging
                    const changedProps = Object.keys(data).filter(key =>
                        JSON.stringify((originalSeat as any)[key]) !== JSON.stringify((updatedSeat as any)[key])
                    );




                    // Replace the seat with the updated version
                    map.seats[seatIndex] = updatedSeat;


                    foundAndUpdated = true;
                    break;
                }
            }

            if (!foundAndUpdated) {
                console.warn(`⚠️ Redux: Could not find seat ${seatId} for comprehensive update`);
            }

            // Update onlineDataForChart for chart rendering
            if (data.statusName !== "LOGGEDOFF" && data.extensionId) {
                // Find if seat already exists in chart data
                const seatIndex = state.onlineDataForChart.findIndex(s => s.agentMapSeatId === seatId);

                if (seatIndex >= 0) {
                    // Update existing seat
                    state.onlineDataForChart[seatIndex] = {
                        ...state.onlineDataForChart[seatIndex],
                        ...data,
                        // Apply display colors
                        fillColor: data.fillColor || getColorForStatus(data.statusName),
                        textColor: data.textColor || getTextColorForStatus(data.statusName),
                        iconColor: data.iconColor || getIconColorForStatus(data.statusName),
                    };

                } else {
                    // Add new seat to chart data - this is the critical path for LOGIN events

                    state.onlineDataForChart.push({
                        ...data,
                        fillColor: data.fillColor || getColorForStatus(data.statusName),
                        textColor: data.textColor || getTextColorForStatus(data.statusName),
                        iconColor: data.iconColor || getIconColorForStatus(data.statusName),
                    });
                }
            } else if (data.statusName === "LOGGEDOFF") {
                // Remove from onlineDataForChart if LOGGEDOFF
                const seatIndexInChart = state.onlineDataForChart.findIndex(s => s.agentMapSeatId === seatId);
                if (seatIndexInChart !== -1) {
                    state.onlineDataForChart.splice(seatIndexInChart, 1);

                }
            }

            // Force state update to trigger component re-renders
            state.lastSocketUpdateTime = Date.now();
            state.lastSocketChartUpdateTime = Date.now();
        },

        // Add to reducers
        updateCallStats(state, action: PayloadAction<{
            callSource: string;
            field: string;
            action: string;
            value: number;
        }>) {
            const { callSource, field, action: actionType, value } = action.payload;



            // Find or create source entry
            let sourceIndex = state.dashboard.callStats.findIndex(
                stats => stats.callSource === callSource
            );

            if (sourceIndex === -1) {
                // Source doesn't exist yet, create it with default values
                const newEntry = {
                    callSource,
                    incomingCalls: 0,
                    waitingForAgent: 0,
                    isTalking: 0,
                    agentReceive: 0,
                    botReceive: 0,
                    missedCall: 0,
                    totalCall: 0
                };

                state.dashboard.callStats.push(newEntry);
                sourceIndex = state.dashboard.callStats.length - 1;

            }

            // Get the current value (or default to 0)
            const currentValue = state.dashboard.callStats[sourceIndex][field] || 0;

            // Calculate the new value based on the action type
            let newValue: number;

            switch (actionType.toLowerCase()) {
                case 'increment':
                    newValue = currentValue + value;

                    break;

                case 'decrement':
                    newValue = Math.max(0, currentValue - value);

                    break;

                case 'set':
                    newValue = value;

                    break;

                default:
                    console.warn(`❓ Redux: Unknown action ${actionType} for field ${field}`);
                    return; // Exit without updating
            }

            // Apply the update to ONLY this field, preserving all other fields
            state.dashboard.callStats[sourceIndex][field] = newValue;

            // Force state update to trigger component re-renders
            state.lastSocketUpdateTime = Date.now();
        },

        // Add a new reducer for handling batched updates
        batchUpdateCallStats(state, action: PayloadAction<{
            callSource: string;
            updates: Record<string, number>;
        }>) {
            const { callSource, updates } = action.payload;



            // Find or create source entry
            let sourceIndex = state.dashboard.callStats.findIndex(
                stats => stats.callSource === callSource
            );

            if (sourceIndex === -1) {
                // Source doesn't exist yet, create it with default values
                const newEntry = {
                    callSource,
                    incomingCalls: 0,
                    waitingForAgent: 0,
                    isTalking: 0,
                    agentReceive: 0,
                    botReceive: 0,
                    missedCall: 0,
                    totalCall: 0
                };

                state.dashboard.callStats.push(newEntry);
                sourceIndex = state.dashboard.callStats.length - 1;

            }

            // Log the previous state of this source


            // Apply only the specified updates, preserving all other fields
            Object.entries(updates).forEach(([field, value]) => {
                const previousValue = state.dashboard.callStats[sourceIndex][field] || 0;
                state.dashboard.callStats[sourceIndex][field] = value;

            });

            // Log the updated state


            // Force state update to trigger component re-renders
            state.lastSocketUpdateTime = Date.now();
        },

        // Add the new reducer inside agentMapSlice.reducers
        setOnlineAgentMapData: (state, action: PayloadAction<AgentMapSeatDto[]>) => {
            const onlineSeats = action.payload;



            const onlineMapData = {
                mapId: -999,
                name: "Online",
                seats: onlineSeats.map(seat => ({
                    ...seat,
                    canCall: seat.canCall !== undefined ? seat.canCall : true,
                    canChat: seat.canChat !== undefined ? seat.canChat : true,
                })) || []
            };

            const onlineMapIndex = state.mapData.findIndex(map => map.mapId === -999);
            if (onlineMapIndex !== -1) {
                state.mapData[onlineMapIndex] = onlineMapData;
            } else {
                state.mapData.push(onlineMapData);
            }

            // ALSO update onlineDataForChart with the same comprehensive data
            state.onlineDataForChart = onlineSeats.map(seat => {
                const displayProps = mapStatusToDisplay(seat.statusName || "UNKNOWN");
                return {
                    ...seat,
                    canCall: seat.canCall !== undefined ? seat.canCall : true,
                    canChat: seat.canChat !== undefined ? seat.canChat : true,
                    fillColor: displayProps.backgroundColor,
                    backgroundColor: displayProps.backgroundColor,
                    textColor: displayProps.textColor,
                    iconColor: displayProps.iconColor,
                };
            });

            // Initialize timers and counters for these seats
            if (onlineSeats && onlineSeats.length > 0) {
                onlineSeats.forEach(seat => {
                    if (seat.agentMapSeatId) {
                        const seatIdKey = seat.agentMapSeatId.toString();
                        state.counters[seatIdKey] = { call: seat.totalCall || 0, chat: seat.totalChat || 0 };
                        if (seat.agentChangeStatusDate) {
                            try {
                                const timestamp = new Date(seat.agentChangeStatusDate).getTime();
                                state.timers[seatIdKey] = isNaN(timestamp) ? Date.now() : timestamp;
                            } catch { state.timers[seatIdKey] = Date.now(); }
                        } else {
                            state.timers[seatIdKey] = Date.now();
                        }
                    }
                });
            }

            state.lastSocketUpdateTime = Date.now(); // For map UI
            state.lastSocketChartUpdateTime = Date.now(); // For chart UI

        },

        // This action is still useful for explicitly setting chart data if needed from other sources,
        // but primary updates should flow through setOnlineAgentMapData and updateSeatComprehensive.
        setOnlineDataForChart: (state, action: PayloadAction<AgentMapSeatDto[]>) => {
            state.onlineDataForChart = action.payload.map(seat => {
                const displayProps = mapStatusToDisplay(seat.statusName || "UNKNOWN");
                return {
                    ...seat,
                    fillColor: displayProps.backgroundColor,
                    backgroundColor: displayProps.backgroundColor,
                    textColor: displayProps.textColor,
                    iconColor: displayProps.iconColor,
                };
            });
            state.lastSocketChartUpdateTime = Date.now();
            // state.silentChartUpdate = true; // This flag might not be necessary if Dashboard memoization is correct

        },

        // Add definitions for previously missing exported actions
        forceUIUpdate: (state, action: PayloadAction<{ timestamp: number }>) => {
            state.lastSocketUpdateTime = action.payload.timestamp;
            // Optionally, if this should also update the chart:
            // state.lastSocketChartUpdateTime = action.payload.timestamp;
        },
        updateChartData: (state, action: PayloadAction<{ timestamp: number }>) => {
            state.lastSocketChartUpdateTime = action.payload.timestamp || Date.now();

        },
        updateMapOnly: (state, action: PayloadAction<{ mapIds: number[] }>) => {
            // This action might be intended to trigger re-evaluation for specific maps.
            // For now, just updating the general map timestamp.
            state.lastSocketUpdateTime = Date.now();
        },

        // Add a direct force update action that only updates lastSocketChartUpdateTime
        forceChartUpdate: (state, action: PayloadAction<{ timestamp: number }>) => {
            state.lastSocketChartUpdateTime = action.payload.timestamp || Date.now();

        },

        // The following actions might need review or removal if their logic is now covered:
        // preserveOnlineMapDataForChartOnly, removeOnlineMap, updateSeatWithChartSupport
        // updateChartData: (state, action: PayloadAction<any>) - This is fine as a generic chart trigger.
        // updateMapOnly: (state, action: PayloadAction<{ mapIds: number[] }>) - This is fine.

        // Remove updateSeatWithChartSupport if updateSeatComprehensive handles chart updates
        // Remove preserveOnlineMapDataForChartOnly and removeOnlineMap if onlineDataForChart is now managed independently
    }
});

export const {
    setSelectedMaps,
    updateMapData,
    setMapData,
    updateSeatStatus,
    updateTimer,
    incrementCounter,
    setLoading,
    setError,
    resetData,
    setSocketUpdateInProgress,
    filterMapData,
    clearSeatData,
    updateSeatComprehensive,
    updateCallStats,
    batchUpdateCallStats,
    setOnlineAgentMapData,
    setOnlineDataForChart,
    forceUIUpdate, // Now defined
    updateChartData, // Now defined
    updateMapOnly, // Now defined
    forceChartUpdate, // Now defined
} = agentMapSlice.actions;

export default agentMapSlice.reducer;

// Helper functions for consistent color mapping
function getColorForStatus(status: string | undefined): string {
    switch (status) {
        case "UNPAUSED":
        case "UNPAUSED_AGENT":
        case "READY":
            return "#06A77D"; // Sẵn sàng
        case "PAUSED":
        case "PAUSE":
            return "#FF9500"; // Tạm nghỉ
        case "ONCALL":
        case "ON_CALL":
        case "CALL":
            return "#D1131D"; // Đàm thoại
        case "RINGING":
        case "RING":
            return "#AF52DE"; // Đang reo
        case "CREATING_TICKET":
        case "CREATE_TICKET":
        case "TICKET":
            return "#1564E8"; // Tạo phiếu
        case "LOGIN":
        case "FAILED_LOGIN":
        case "LOGGEDIN":
            return "#E8F4FC"; // Không bật - light blue
        case "LOGGEDOFF":
        case "UNKNOWN":
        default:
            return "#EBEBED"; // Gray for LOGGEDOFF/UNKNOWN
    }
}

function getTextColorForStatus(status: string | undefined): string {
    switch (status) {
        case "UNPAUSED":
        case "UNPAUSED_AGENT":
        case "READY":
        case "PAUSED":
        case "PAUSE":
        case "ONCALL":
        case "ON_CALL":
        case "CALL":
        case "RINGING":
        case "RING":
        case "CREATING_TICKET":
        case "CREATE_TICKET":
        case "TICKET":
            return "#FFFFFF"; // White text for colored backgrounds
        case "LOGIN":
        case "FAILED_LOGIN":
        case "LOGGEDIN":
        case "LOGGEDOFF":
        case "UNKNOWN":
        default:
            return "#000000"; // Black text for light backgrounds
    }
}

function getIconColorForStatus(status: string | undefined): string {
    switch (status) {
        case "UNPAUSED":
        case "UNPAUSED_AGENT":
        case "READY":
        case "PAUSED":
        case "PAUSE":
        case "ONCALL":
        case "ON_CALL":
        case "CALL":
        case "RINGING":
        case "RING":
        case "CREATING_TICKET":
        case "CREATE_TICKET":
        case "TICKET":
            return "#FFFFFF"; // White icons for colored backgrounds
        case "LOGIN":
        case "FAILED_LOGIN":
        case "LOGGEDIN":
            return "#000000"; // Black icons for light backgrounds
        case "LOGGEDOFF":
        case "UNKNOWN":
        default:
            return "#B2B9C4"; // Gray icons for LOGGEDOFF
    }
}
